import { useState, useContext, useEffect } from "react";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
// @mui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import YouTubeIcon from "@mui/icons-material/YouTube";

import Avatar from "@mui/material/Avatar";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import SuiBadge from "components/SuiBadge";

import toast from "react-hot-toast";
import moment from "moment";
import { useForm, Controller } from "react-hook-form";

// Images
import { getBase64, matchYoutubeUrl, handleUploadFiles } from "utils/imageHelper";

import { StoreContext } from "context/store";
import { AccountContext } from "context/account";
import api from "services/api";
import { nanoid } from "nanoid";

import DropZone from "components/UpStyled/DropZone";

const MAX_IMAGE_COUNT = 10;

const UploadGalleryModal = ({
  handleRefresh,
  visible,
  handleOk,
  handleClose,
  slot = "gallery_0",
  replace = true,
}) => {
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [addLinkVisible, setAddLinkVisible] = useState(false);
  const [link, setLink] = useState();

  const account = useContext(AccountContext);

  useEffect(() => {
    if (visible) {
      let old_portfolio = account?.metadata?.portfolio_content || {};
      const gallery = old_portfolio[slot] || [];
      let images = gallery.map((x) => x.img);

      setImageFiles(images);
    }
  }, [visible]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      const myImageFiles = (await handleUploadFiles(imageFiles)) || [];

      if (account.id) {
        let old_portfolio = account?.metadata?.portfolio_content || {};
        if (!old_portfolio[slot]) {
          old_portfolio[slot] = [];
        }

        if (replace) {
          old_portfolio[slot] = [];
        }
        //add new item
        for (let i = 0; i < myImageFiles.length; i++) {
          old_portfolio[slot].push({
            img: myImageFiles[i],
            type: "gallery",
          });
        }

        let payload = {
          metadata: {
            ...account?.metadata,
            portfolio_content: old_portfolio,
          },
        };

        const { data } = await api.account.update(account.id, payload);
        setUploading(false);
        handleRefresh();
        handleClose();
        toast.success("Update gallery successfully!");
      } else {
        toast.error("Can't find the user!");
      }
    } catch (err) {
      console.log(err);
      setUploading(false);

      toast.error("Update gallery failed!");
    }
  };

  const handleAddYoutubeLink = (e) => {
    e.preventDefault();
    if (matchYoutubeUrl(link)) {
      setImageFiles([...imageFiles, { url: link, name: link, type: "video" }]);
      setAddLinkVisible(false);
    } else {
      toast.error("Not a valid Youtube URL!");
    }
  };

  return (
    <Dialog
      open={visible}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullWidth={true}
    >
      <DialogTitle id="alert-dialog-title">
        Images & Videos
        <SuiTypography variant="body1" color="text">
          These may include idea photos, moodboard, style photos etc.
        </SuiTypography>
        <IconButton
          aria-label="close"
          onClick={() => handleClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
          <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
          <SuiTypography variant="h6" fontWeight="medium" color="info">
            Uploading supporting files...
          </SuiTypography>
        </Backdrop>
        <SuiBox display="flex" justifyContent="space-between">
          <SuiBox>
            <SuiBox mt={1} ml={0.5} display="flex" itemsAlign="center" gap={1}>
              <ImageIcon fontSize="small" color="text" />
              <SuiTypography component="label" variant="body1" textTransform="none" color="text">
                {imageFiles.length} images/videos have been selected.
              </SuiTypography>{" "}
            </SuiBox>
          </SuiBox>
          <SuiBox>
            <SuiButton
              size="small"
              variant="gradient"
              color="dark"
              onClick={() => setAddLinkVisible(true)}
            >
              <div className="flex items-center justify-center">
                <div className="mr-1 mt-1">
                  <AddIcon fontSize="medium" />
                </div>
                <SuiTypography variant="text" color="light" textTransform="uppercase">
                  Youtube Link
                </SuiTypography>
              </div>
            </SuiButton>
          </SuiBox>
        </SuiBox>
        <div>
          <SuiBox mt={2}>
            <DropZone
              files={imageFiles}
              setfiles={setImageFiles}
              options={{
                accept: { "image/*": [".jpeg", ".png"] },
              }}
              fileCardStyle={{ height: 160, width: 128 }}
            />
          </SuiBox>

          <SuiBox
            pt={3}
            px={3}
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton
                  variant="gradient"
                  color="light"
                  sx={{ height: "100%" }}
                  onClick={handleClose}
                >
                  Cancel
                </SuiButton>
              </SuiBox>
            </SuiBox>

            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton
                  variant="gradient"
                  color="dark"
                  sx={{ height: "100%" }}
                  onClick={handleOk ? handleOk : handleSave}
                >
                  save
                </SuiButton>
              </SuiBox>
            </SuiBox>
          </SuiBox>
        </div>
        {addLinkVisible ? (
          <Dialog
            sx={{ "& .MuiDialog-paper": { width: "80%", maxHeight: 435 } }}
            maxWidth="xs"
            open={addLinkVisible}
          >
            <DialogTitle>Add Youtube Link</DialogTitle>
            <DialogContent dividers>
              <SuiBox key={"youtube_link"} py={1} pr={2}>
                <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                  Youtube Link: &nbsp;
                </SuiTypography>
                <SuiTypography variant="button" fontWeight="regular" color="text">
                  <SuiInput
                    icon={{ direction: "left", component: <YouTubeIcon /> }}
                    placeholder="https://www.youtube.com/watch?v=xxxxxx"
                    required
                    onChange={(e) => setLink(e.target.value)}
                    name="youtube_link"
                  />
                </SuiTypography>
              </SuiBox>
            </DialogContent>
            <DialogActions>
              <SuiButton
                autoFocus
                variant="gradient"
                color="light"
                onClick={() => setAddLinkVisible(false)}
              >
                Cancel
              </SuiButton>
              <SuiButton onClick={handleAddYoutubeLink} variant="gradient" color="dark">
                Add
              </SuiButton>
            </DialogActions>
          </Dialog>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default UploadGalleryModal;
